import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  BACKEND_API_BASE_URL,
  CLOUD_NAME,
  CLOUD_PRESET,
  additionalLogging,
  isOfflineMode,
} from "./constant";
import "./unloading-checklist-styles.css";

const FORM1_ROWS = [
  { key: "routeCondition", label: "Route condition", statusOptions: ["OK", "Not OK"], minPhotos: 4 },
  { key: "siteCondition", label: "Site condition", statusOptions: ["OK", "Not OK"], minPhotos: 4 },
  { key: "approachRoad", label: "Approach road", statusOptions: ["OK", "Not OK"], minPhotos: 4 },
  { key: "boundaryWall", label: "Boundary wall", statusOptions: ["Avail", "Not Avail"], minPhotos: 4 },
  { key: "securityGuard", label: "Security guard", statusOptions: ["Avail", "Not Avail"], minPhotos: 0 },
];

const FORM2_ROWS = [
  { key: "entryToSite", label: "Entry to TSS/SP/SSP/SS", statusOptions: ["Yes", "No"], minPhotos: 4 },
  { key: "trailerMovement", label: "Trailer Movement suitable or not", statusOptions: ["Yes", "No"], minPhotos: 4 },
  { key: "hydraBoom", label: "Hydra/Boom Movement", statusOptions: ["Yes", "No"], minPhotos: 4 },
  { key: "unloadingPoint", label: "Unloading Point", statusOptions: ["Plinth", "W/O Plinth"], minPhotos: 0 },
  { key: "dateTrailerReached", label: "Date of Trailer Reached", statusType: "date", minPhotos: 0 },
  { key: "dateOfUnloading", label: "Date of Unloading", statusType: "date", minPhotos: 0 },
  { key: "aestheticRemarks", label: "Aesthetic / Any Remarks", statusType: "text", statusPlaceholder: "Mention in details", minPhotos: 0 },
  { key: "wheelLocking", label: "Wheel Locking", statusOptions: ["Yes", "No"], minPhotos: 4, photoHint: "All 4 wheel locking photos" },
  { key: "allSealChecks", label: "All Seal Checks", statusOptions: ["OK", "Not OK"], minPhotos: 0 },
  { key: "afterUnloadingPhotos", label: "After unloading photos", statusType: "none", minPhotos: 1, photoHint: "Upload Photos" },
  { key: "togLevelCheck", label: "TOG Level Check as per dispatch", statusOptions: ["OK", "Not OK"], minPhotos: 1 },
  { key: "trsCoveringPhoto", label: "After All check TRS Covering photo", statusType: "none", minPhotos: 1 },
  { key: "dispatchDocuments", label: "Sign and Stamp Copy of all documents as per dispatch", statusOptions: ["Main Tank"], minPhotos: 0, allowPdf: true, photoHint: "PDF Uploads" },
];

const FORM3_TOP_ROWS = [
  { key: "accessoriesUnloadingPoint", label: "Accessories unloading point", statusOptions: ["Site", "Store"], minPhotos: 1 },
  { key: "dateOfUnloading", label: "Date of Unloading", statusType: "date", minPhotos: 0 },
  { key: "storagePhotos", label: "Storage Photos", statusType: "none", minPhotos: 1 },
];

const DEFAULT_ACCESSORIES = [
  "TR Wheels",
  "HV Bushings",
  "LV Bushings",
  "Neutral Bushings",
  "Turrets",
  "Radiators",
  "Conservator",
  "Conservator Frame",
  "Pipelines/RFBD",
  "Fan Frame",
  "Fan Frame Support",
  "Accessories Boxes",
  "",
  "",
  "",
];

const emptyRow = () => ({ status: "", remarks: "", date: "", checkedBy: "" });
const emptyAccessory = (desc = "") => ({
  packingCaseNumber: "",
  materialDescription: desc,
  qtyAsPerChallan: "",
  qtyAsReceived: "",
  date: "",
  checkedBy: "",
});
const emptySign = () => ({ signature: "", date: "", name: "" });

const deptApiMap = {
  "Auto Transformer": {
    data: "/api/autoData/setTable",
    get: "/api/autoData/getTable",
    company: "/api/autocompany/updateFormsCompleted",
    stage1Forms: 5,
  },
  "Traction Transformer": {
    data: "/api/tractionData/setTable",
    get: "/api/tractionData/getTable",
    company: "/api/tractioncompany/updateFormsCompleted",
    stage1Forms: 7,
  },
  "V Connected 63 MVA Transformer": {
    data: "/api/vconnectData/setTable",
    get: "/api/vconnectData/getTable",
    company: "/api/vconnectcompany/updateFormsCompleted",
    stage1Forms: 8,
  },
};

function SimpleSignaturePad({ value, onChange, label }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL());
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="ul-sign-pad">
      <div className="ul-sign-label">{label}</div>
      <canvas
        ref={canvasRef}
        width={280}
        height={90}
        className="ul-sign-canvas"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button type="button" className="ul-clear-btn" onClick={clear}>
        Clear
      </button>
    </div>
  );
}

function PhotoUploader({ photoKey, files, urls, onFilesChange, hint, accept = "image/*", multiple = true }) {
  const existing = Array.isArray(urls?.[photoKey])
    ? urls[photoKey]
    : urls?.[photoKey]
      ? [urls[photoKey]]
      : [];
  const pending = files?.[photoKey] || [];

  return (
    <div className="ul-photo-cell">
      <div className="ul-photo-hint">{hint || "Upload photos"}</div>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          const selected = Array.from(e.target.files || []);
          onFilesChange(photoKey, selected);
        }}
      />
      {(existing.length > 0 || pending.length > 0) && (
        <div className="ul-photo-count">
          {existing.length} saved · {pending.length} new
        </div>
      )}
    </div>
  );
}

const UnloadingChecklistForms = ({
  departmentName,
  projectName,
  companyName,
  onFormSubmit,
  onBack,
  setSelectedMainCompany,
}) => {
  const [formIndex, setFormIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [photoFiles, setPhotoFiles] = useState({});
  const skipNextLoadRef = useRef(false);
  const apis = deptApiMap[departmentName] || deptApiMap["Auto Transformer"];

  const [form1, setForm1] = useState({
    vpesRepresentative: "",
    headerDate: "",
    customerRepresentative: "",
    contactNo: "",
    rows: Object.fromEntries(FORM1_ROWS.map((r) => [r.key, emptyRow()])),
    photos: {},
    vpesSign: emptySign(),
    customerSign: emptySign(),
  });

  const [form2, setForm2] = useState({
    rows: Object.fromEntries(FORM2_ROWS.map((r) => [r.key, emptyRow()])),
    photos: {},
    documents: {},
    vpesSign: emptySign(),
    representativeSign: emptySign(),
  });

  const [form3, setForm3] = useState({
    topRows: Object.fromEntries(FORM3_TOP_ROWS.map((r) => [r.key, emptyRow()])),
    accessories: DEFAULT_ACCESSORIES.map((d) => emptyAccessory(d)),
    remark: "",
    photos: {},
    vpesSign: emptySign(),
    customerSign: emptySign(),
  });

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const response = await axios.post(`${BACKEND_API_BASE_URL}${apis.get}`, {
          projectName,
          companyName,
          stage: 0,
          formNumber: formIndex + 1,
        });
        if (cancelled) return;
        const data = response.data?.data;
        if (!data || typeof data !== "object") return;
        if (formIndex === 0) {
          setForm1((prev) => ({
            ...prev,
            ...data,
            rows: { ...prev.rows, ...(data.rows || {}) },
            vpesSign: { ...emptySign(), ...(data.vpesSign || {}) },
            customerSign: { ...emptySign(), ...(data.customerSign || {}) },
            photos: data.photos || {},
          }));
        } else if (formIndex === 1) {
          setForm2((prev) => ({
            ...prev,
            ...data,
            rows: { ...prev.rows, ...(data.rows || {}) },
            vpesSign: { ...emptySign(), ...(data.vpesSign || {}) },
            representativeSign: { ...emptySign(), ...(data.representativeSign || {}) },
            photos: data.photos || {},
            documents: data.documents || {},
          }));
        } else {
          setForm3((prev) => ({
            ...prev,
            ...data,
            topRows: { ...prev.topRows, ...(data.topRows || {}) },
            accessories:
              Array.isArray(data.accessories) && data.accessories.length
                ? data.accessories
                : prev.accessories,
            vpesSign: { ...emptySign(), ...(data.vpesSign || {}) },
            customerSign: { ...emptySign(), ...(data.customerSign || {}) },
            photos: data.photos || {},
          }));
        }
      } catch (err) {
        if (additionalLogging) {
          console.log("No existing unloading form data yet", err?.response?.status);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [formIndex, projectName, companyName, apis.get]);

  const setPhotoFilesForKey = (key, files) => {
    setPhotoFiles((prev) => ({ ...prev, [key]: files }));
  };

  const uploadSingleFile = async (key, file, index) => {
    // Frontend-only demo: keep photos as local data URLs (no Cloudinary / backend)
    if (isOfflineMode()) {
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error(`Could not read file for ${key}`));
        reader.readAsDataURL(file);
      });
    }

    const imgData = new FormData();
    const publicId = `ul_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`;
    imgData.append("file", file);
    imgData.append("upload_preset", CLOUD_PRESET);
    imgData.append("cloud_name", CLOUD_NAME);
    imgData.append("public_id", publicId);
    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
      method: "POST",
      body: imgData,
    });
    if (!res.ok) throw new Error(`Upload failed for ${key}`);
    const json = await res.json();
    return json.secure_url || null;
  };

  /** Upload all selected files in parallel (much faster than one-by-one). */
  const uploadFiles = async (filesMap) => {
    const jobs = [];
    for (const [key, files] of Object.entries(filesMap)) {
      if (!files?.length) continue;
      files.forEach((file, index) => {
        jobs.push(
          uploadSingleFile(key, file, index).then((url) => ({ key, url }))
        );
      });
    }

    if (!jobs.length) return {};

    setSaveStatus(`Uploading ${jobs.length} file(s)...`);
    const results = await Promise.all(jobs);
    const uploaded = {};
    for (const { key, url } of results) {
      if (!url) continue;
      if (!uploaded[key]) uploaded[key] = [];
      uploaded[key].push(url);
    }
    // Keep single-url keys as string for simpler schema use
    for (const key of Object.keys(uploaded)) {
      if (uploaded[key].length === 1) uploaded[key] = uploaded[key][0];
    }
    return uploaded;
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus("Saving...");
    try {
      const uploaded = await uploadFiles(photoFiles);
      let payload;

      if (formIndex === 0) {
        payload = {
          ...form1,
          photos: { ...form1.photos, ...uploaded },
        };
        setForm1(payload);
      } else if (formIndex === 1) {
        const docKeys = FORM2_ROWS.filter((r) => r.allowPdf).map((r) => r.key);
        const photoUploads = {};
        const docUploads = {};
        for (const [k, v] of Object.entries(uploaded)) {
          if (docKeys.includes(k)) docUploads[k] = v;
          else photoUploads[k] = v;
        }
        payload = {
          ...form2,
          photos: { ...form2.photos, ...photoUploads },
          documents: { ...form2.documents, ...docUploads },
        };
        setForm2(payload);
      } else {
        payload = {
          ...form3,
          photos: { ...form3.photos, ...uploaded },
        };
        setForm3(payload);
      }

      const formsCompleted = formIndex + 1;
      const isLast = formIndex === 2;

      setSaveStatus("Saving form data...");
      const saveData = axios.post(`${BACKEND_API_BASE_URL}${apis.data}`, {
        projectName,
        companyName,
        stage: 0,
        formNumber: formIndex + 1,
        ...payload,
      });

      const saveProgress = axios.post(`${BACKEND_API_BASE_URL}${apis.company}`, {
        projectName,
        companyName,
        formsCompleted,
        ...(isLast
          ? { completeUnloading: true, totalForms: apis.stage1Forms }
          : {}),
      });

      // Move UI forward immediately while requests finish
      setPhotoFiles({});
      if (isLast) {
        if (setSelectedMainCompany) {
          setSelectedMainCompany((prev) => {
            if (!prev || prev.companyName !== companyName) return prev;
            return {
              ...prev,
              companyProjects: (prev.companyProjects || []).map((project) => {
                if (project.name !== projectName) return project;
                return {
                  ...project,
                  stage: 1,
                  formsCompleted: 0,
                  totalForms: apis.stage1Forms,
                  status: "in-progress",
                  stageApprovals: { ...(project.stageApprovals || {}), 0: true },
                  submittedStages: { ...(project.submittedStages || {}), 0: true },
                };
              }),
            };
          });
        }
        await Promise.all([saveData, saveProgress]);
        onFormSubmit?.(0, payload);
      } else {
        if (setSelectedMainCompany) {
          setSelectedMainCompany((prev) => {
            if (!prev || prev.companyName !== companyName) return prev;
            return {
              ...prev,
              companyProjects: (prev.companyProjects || []).map((project) =>
                project.name === projectName
                  ? { ...project, formsCompleted }
                  : project
              ),
            };
          });
        }
        skipNextLoadRef.current = true;
        setFormIndex((i) => i + 1);
        setSaving(false);
        setSaveStatus("");
        // Finish network in background so next form shows instantly
        Promise.all([saveData, saveProgress]).catch((error) => {
          console.error("Background save failed", error);
          alert("Form may not have saved. Please go back and save again.");
        });
        return;
      }
    } catch (error) {
      console.error("Failed to save unloading form", error);
      alert("Failed to save form. Please try again.");
    } finally {
      setSaving(false);
      setSaveStatus("");
    }
  };

  const updateRow = (setter, bag, key, field, value) => {
    setter((prev) => ({
      ...prev,
      [bag]: {
        ...prev[bag],
        [key]: { ...prev[bag][key], [field]: value },
      },
    }));
  };

  const titles = [
    "Form 1 — Site condition at time of unloading",
    "Form 2 — Main Tank Checklist",
    "Form 3 — Protocol for Accessories Checking",
  ];

  const renderStatusCell = (rowDef, value, onChange) => {
    if (rowDef.statusType === "none") return <span className="ul-muted">—</span>;
    if (rowDef.statusType === "date") {
      return (
        <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      );
    }
    if (rowDef.statusType === "text") {
      return (
        <input
          type="text"
          placeholder={rowDef.statusPlaceholder || ""}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select</option>
        {(rowDef.statusOptions || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="ul-container fade-in">
      <div className="ul-header">
        <div>
          <p className="ul-brand">VISHVAS POWER</p>
          <h2>Unloading Checklist</h2>
          <p className="ul-subtitle">{titles[formIndex]}</p>
          <p className="ul-meta">
            {companyName} · {projectName} · Form {formIndex + 1} of 3
          </p>
        </div>
        <button type="button" className="ul-back-btn" onClick={onBack}>
          ← Back
        </button>
      </div>

      <div className="ul-progress">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`ul-step ${i === formIndex ? "active" : ""} ${i < formIndex ? "done" : ""}`}>
            {i + 1}
          </div>
        ))}
      </div>

      <div className="ul-card">
        {formIndex === 0 && (
          <>
            <div className="ul-header-grid">
              <label>
                VPES Representative
                <input
                  value={form1.vpesRepresentative}
                  onChange={(e) => setForm1({ ...form1, vpesRepresentative: e.target.value })}
                />
              </label>
              <label>
                Date
                <input
                  type="date"
                  value={form1.headerDate}
                  onChange={(e) => setForm1({ ...form1, headerDate: e.target.value })}
                />
              </label>
              <label>
                Customer Representative
                <input
                  value={form1.customerRepresentative}
                  onChange={(e) => setForm1({ ...form1, customerRepresentative: e.target.value })}
                />
              </label>
              <label>
                Contact No.
                <input
                  value={form1.contactNo}
                  onChange={(e) => setForm1({ ...form1, contactNo: e.target.value })}
                />
              </label>
            </div>

            <div className="ul-table-wrap">
              <table className="ul-table">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Check Points</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    <th>Photo Required</th>
                    <th>Date</th>
                    <th>Checked By</th>
                  </tr>
                </thead>
                <tbody>
                  {FORM1_ROWS.map((row, idx) => (
                    <tr key={row.key}>
                      <td>{idx + 1}</td>
                      <td>{row.label}</td>
                      <td>
                        {renderStatusCell(row, form1.rows[row.key]?.status, (v) =>
                          updateRow(setForm1, "rows", row.key, "status", v)
                        )}
                      </td>
                      <td>
                        <input
                          value={form1.rows[row.key]?.remarks || ""}
                          onChange={(e) =>
                            updateRow(setForm1, "rows", row.key, "remarks", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        {row.minPhotos > 0 ? (
                          <PhotoUploader
                            photoKey={row.key}
                            files={photoFiles}
                            urls={form1.photos}
                            onFilesChange={setPhotoFilesForKey}
                            hint={`Photos (optional, recommended ${row.minPhotos}+)`}
                          />
                        ) : (
                          <span className="ul-muted">—</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="date"
                          value={form1.rows[row.key]?.date || ""}
                          onChange={(e) =>
                            updateRow(setForm1, "rows", row.key, "date", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={form1.rows[row.key]?.checkedBy || ""}
                          onChange={(e) =>
                            updateRow(setForm1, "rows", row.key, "checkedBy", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ul-sign-row">
              <div>
                <h4>VPES Representative</h4>
                <SimpleSignaturePad
                  label="Sign"
                  value={form1.vpesSign.signature}
                  onChange={(signature) =>
                    setForm1({ ...form1, vpesSign: { ...form1.vpesSign, signature } })
                  }
                />
                <label>
                  Date
                  <input
                    type="date"
                    value={form1.vpesSign.date}
                    onChange={(e) =>
                      setForm1({ ...form1, vpesSign: { ...form1.vpesSign, date: e.target.value } })
                    }
                  />
                </label>
              </div>
              <div>
                <h4>Customer Representative</h4>
                <SimpleSignaturePad
                  label="Sign"
                  value={form1.customerSign.signature}
                  onChange={(signature) =>
                    setForm1({ ...form1, customerSign: { ...form1.customerSign, signature } })
                  }
                />
                <label>
                  Date
                  <input
                    type="date"
                    value={form1.customerSign.date}
                    onChange={(e) =>
                      setForm1({
                        ...form1,
                        customerSign: { ...form1.customerSign, date: e.target.value },
                      })
                    }
                  />
                </label>
              </div>
            </div>
          </>
        )}

        {formIndex === 1 && (
          <>
            <div className="ul-table-wrap">
              <table className="ul-table">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Checklist Item</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    <th>Photo Required</th>
                    <th>Date</th>
                    <th>Checked By</th>
                  </tr>
                </thead>
                <tbody>
                  {FORM2_ROWS.map((row, idx) => (
                    <tr key={row.key}>
                      <td>{idx + 1}</td>
                      <td>{row.label}</td>
                      <td>
                        {renderStatusCell(row, form2.rows[row.key]?.status, (v) =>
                          updateRow(setForm2, "rows", row.key, "status", v)
                        )}
                      </td>
                      <td>
                        <input
                          value={form2.rows[row.key]?.remarks || ""}
                          onChange={(e) =>
                            updateRow(setForm2, "rows", row.key, "remarks", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        {row.minPhotos > 0 || row.allowPdf ? (
                          <PhotoUploader
                            photoKey={row.key}
                            files={photoFiles}
                            urls={row.allowPdf ? form2.documents : form2.photos}
                            onFilesChange={setPhotoFilesForKey}
                            hint={row.photoHint || (row.allowPdf ? "PDF Uploads" : "Upload Photos")}
                            accept={row.allowPdf ? "application/pdf,image/*" : "image/*"}
                          />
                        ) : (
                          <span className="ul-muted">—</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="date"
                          value={form2.rows[row.key]?.date || ""}
                          onChange={(e) =>
                            updateRow(setForm2, "rows", row.key, "date", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={form2.rows[row.key]?.checkedBy || ""}
                          onChange={(e) =>
                            updateRow(setForm2, "rows", row.key, "checkedBy", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ul-sign-row">
              <div>
                <h4>VPES Representative</h4>
                <SimpleSignaturePad
                  label="Sign"
                  value={form2.vpesSign.signature}
                  onChange={(signature) =>
                    setForm2({ ...form2, vpesSign: { ...form2.vpesSign, signature } })
                  }
                />
                <label>
                  Date
                  <input
                    type="date"
                    value={form2.vpesSign.date}
                    onChange={(e) =>
                      setForm2({ ...form2, vpesSign: { ...form2.vpesSign, date: e.target.value } })
                    }
                  />
                </label>
              </div>
              <div>
                <h4>Representative</h4>
                <SimpleSignaturePad
                  label="Sign"
                  value={form2.representativeSign.signature}
                  onChange={(signature) =>
                    setForm2({
                      ...form2,
                      representativeSign: { ...form2.representativeSign, signature },
                    })
                  }
                />
                <label>
                  Date
                  <input
                    type="date"
                    value={form2.representativeSign.date}
                    onChange={(e) =>
                      setForm2({
                        ...form2,
                        representativeSign: {
                          ...form2.representativeSign,
                          date: e.target.value,
                        },
                      })
                    }
                  />
                </label>
              </div>
            </div>
          </>
        )}

        {formIndex === 2 && (
          <>
            <div className="ul-table-wrap">
              <table className="ul-table">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Checklist Item</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    <th>Photo Required</th>
                    <th>Date</th>
                    <th>Checked By</th>
                  </tr>
                </thead>
                <tbody>
                  {FORM3_TOP_ROWS.map((row, idx) => (
                    <tr key={row.key}>
                      <td>{idx + 1}</td>
                      <td>{row.label}</td>
                      <td>
                        {renderStatusCell(row, form3.topRows[row.key]?.status, (v) =>
                          updateRow(setForm3, "topRows", row.key, "status", v)
                        )}
                      </td>
                      <td>
                        <input
                          value={form3.topRows[row.key]?.remarks || ""}
                          onChange={(e) =>
                            updateRow(setForm3, "topRows", row.key, "remarks", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        {row.minPhotos > 0 ? (
                          <PhotoUploader
                            photoKey={row.key}
                            files={photoFiles}
                            urls={form3.photos}
                            onFilesChange={setPhotoFilesForKey}
                            hint="Upload Photos"
                          />
                        ) : (
                          <span className="ul-muted">—</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="date"
                          value={form3.topRows[row.key]?.date || ""}
                          onChange={(e) =>
                            updateRow(setForm3, "topRows", row.key, "date", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={form3.topRows[row.key]?.checkedBy || ""}
                          onChange={(e) =>
                            updateRow(setForm3, "topRows", row.key, "checkedBy", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="ul-section-title">Accessories Inventory</h3>
            <div className="ul-table-wrap">
              <table className="ul-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Packing Case Number</th>
                    <th>Material Description</th>
                    <th>Qty as per Challan</th>
                    <th>Qty as Received</th>
                    <th>Date</th>
                    <th>Checked By</th>
                  </tr>
                </thead>
                <tbody>
                  {form3.accessories.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <input
                          value={item.packingCaseNumber}
                          onChange={(e) => {
                            const next = [...form3.accessories];
                            next[idx] = { ...next[idx], packingCaseNumber: e.target.value };
                            setForm3({ ...form3, accessories: next });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          value={item.materialDescription}
                          onChange={(e) => {
                            const next = [...form3.accessories];
                            next[idx] = { ...next[idx], materialDescription: e.target.value };
                            setForm3({ ...form3, accessories: next });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          value={item.qtyAsPerChallan}
                          onChange={(e) => {
                            const next = [...form3.accessories];
                            next[idx] = { ...next[idx], qtyAsPerChallan: e.target.value };
                            setForm3({ ...form3, accessories: next });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          value={item.qtyAsReceived}
                          onChange={(e) => {
                            const next = [...form3.accessories];
                            next[idx] = { ...next[idx], qtyAsReceived: e.target.value };
                            setForm3({ ...form3, accessories: next });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={item.date}
                          onChange={(e) => {
                            const next = [...form3.accessories];
                            next[idx] = { ...next[idx], date: e.target.value };
                            setForm3({ ...form3, accessories: next });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          value={item.checkedBy}
                          onChange={(e) => {
                            const next = [...form3.accessories];
                            next[idx] = { ...next[idx], checkedBy: e.target.value };
                            setForm3({ ...form3, accessories: next });
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <label className="ul-remark">
              Remark :-
              <textarea
                rows={3}
                value={form3.remark}
                onChange={(e) => setForm3({ ...form3, remark: e.target.value })}
              />
            </label>

            <div className="ul-sign-row">
              <div>
                <h4>VPES Representative</h4>
                <SimpleSignaturePad
                  label="Sign"
                  value={form3.vpesSign.signature}
                  onChange={(signature) =>
                    setForm3({ ...form3, vpesSign: { ...form3.vpesSign, signature } })
                  }
                />
                <label>
                  Date
                  <input
                    type="date"
                    value={form3.vpesSign.date}
                    onChange={(e) =>
                      setForm3({ ...form3, vpesSign: { ...form3.vpesSign, date: e.target.value } })
                    }
                  />
                </label>
              </div>
              <div>
                <h4>Customer Representative</h4>
                <SimpleSignaturePad
                  label="Sign"
                  value={form3.customerSign.signature}
                  onChange={(signature) =>
                    setForm3({ ...form3, customerSign: { ...form3.customerSign, signature } })
                  }
                />
                <label>
                  Date
                  <input
                    type="date"
                    value={form3.customerSign.date}
                    onChange={(e) =>
                      setForm3({
                        ...form3,
                        customerSign: { ...form3.customerSign, date: e.target.value },
                      })
                    }
                  />
                </label>
              </div>
            </div>
          </>
        )}

        <div className="ul-actions">
          <button
            type="button"
            className="ul-secondary-btn"
            disabled={formIndex === 0 || saving}
            onClick={() => setFormIndex((i) => Math.max(0, i - 1))}
          >
            Previous
          </button>
          <button type="button" className="ul-primary-btn" disabled={saving} onClick={handleSave}>
            {saving
              ? saveStatus || "Saving..."
              : formIndex === 2
                ? "Complete Unloading & Start Stage 1"
                : "Save & Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnloadingChecklistForms;
